import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { ZoomIn, Check, X } from "lucide-react";
import { Spinner } from "./common";

async function getCroppedBlob(imageSrc, cropPixels) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });
  const size = Math.min(cropPixels.width, cropPixels.height);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, cropPixels.x, cropPixels.y, size, size, 0, 0, size, size);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92));
}

export function CropModal({ imageSrc, onCancel, onDone }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onComplete = useCallback((_, areaPixels) => setPixels(areaPixels), []);

  const confirm = async () => {
    if (!pixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(imageSrc, pixels);
      await onDone(blob);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/70 p-4" data-testid="crop-modal">
      <div className="w-full max-w-sm rounded-3xl bg-card overflow-hidden border border-border">
        <div className="relative h-72 bg-black">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onComplete}
          />
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3">
            <ZoomIn size={18} className="text-muted-foreground shrink-0" />
            <input data-testid="crop-zoom" type="range" min={1} max={3} step={0.01} value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full accent-[color:hsl(var(--primary))]" />
          </div>
          <p className="text-xs text-muted-foreground text-center">Drag to reposition and zoom to pick the part you want on your profile.</p>
          <div className="flex gap-3">
            <button data-testid="crop-cancel" onClick={onCancel} disabled={busy}
              className="flex-1 h-12 rounded-xl bg-secondary font-semibold flex items-center justify-center gap-1.5"><X size={16} /> Cancel</button>
            <button data-testid="crop-confirm" onClick={confirm} disabled={busy || !pixels}
              className="flex-1 h-12 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60">
              {busy ? <Spinner className="w-5 h-5" /> : <><Check size={16} /> Use photo</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
